import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const orderNumber = params.get("order");
  const { clearCart } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | error | timeout
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    let active = true;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (!active) return;
        if (res.data.payment_status === "paid") {
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep polling on transient errors
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center bg-[#F4EFE8] px-6 py-24">
      <div className="text-center max-w-md" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto mb-6 text-[#8a6d3b] animate-spin" />
            <h1 className="font-serif-display text-[34px] font-medium mb-3">Confirming your payment…</h1>
            <p className="text-[15px] text-[#1A1A1A]/70">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} strokeWidth={1.2} className="mx-auto mb-6 text-[#8a6d3b]" />
            <h1 className="font-serif-display text-[40px] font-medium mb-3" data-testid="payment-success-title">Thank you!</h1>
            <p className="text-[15px] text-[#1A1A1A]/70 mb-2">
              Your FlowLux order {orderNumber && <strong>{orderNumber}</strong>} is confirmed and paid.
            </p>
            <p className="text-[15px] text-[#1A1A1A]/70 mb-8">
              A confirmation email is on its way. Your glow up starts now.
            </p>
            <Link to="/" className="btn-lux inline-block px-10 py-4 text-[12px] tracking-[0.22em] uppercase" data-testid="continue-shopping-btn">
              Continue Shopping
            </Link>
          </>
        )}
        {(state === "error" || state === "timeout") && (
          <>
            <XCircle size={52} strokeWidth={1.2} className="mx-auto mb-6 text-[#a33]" />
            <h1 className="font-serif-display text-[34px] font-medium mb-3">
              {state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="text-[15px] text-[#1A1A1A]/70 mb-8">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, your order will confirm shortly and you'll get an email."
                : "We couldn't confirm your payment. You have not been charged. Please try again."}
            </p>
            <Link to="/checkout" className="btn-lux inline-block px-10 py-4 text-[12px] tracking-[0.22em] uppercase">
              Back to Checkout
            </Link>
          </>
        )}
      </div>
    </main>
  );
};

export default PaymentSuccess;
